import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Label,
  MenuToggle,
  Pagination,
  SearchInput,
  Select,
  SelectList,
  SelectOption,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { ExclamationCircleIcon, SearchIcon, SyncAltIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useNetworkAppliances } from '../../../hooks/useNetworkAppliances';
import type { NetworkAppliance, NetworkApplianceKind } from '../../../types/models/NetworkAppliance';
import type { NetworkApplianceResource } from '../../../types/api/ApiResponseNetworkAppliances';
import renderStatusLabel from '../Servers/serverStatusLabel';

type KindFilter = 'all' | NetworkApplianceKind;

const kindFilterLabel: Record<KindFilter, string> = {
  all: 'All kinds',
  device: 'Device',
  vm: 'VM',
};

const renderKindLabel = (kind: NetworkApplianceKind): React.ReactNode =>
  kind === 'vm' ? <Label color="purple">VM</Label> : <Label color="blue">Device</Label>;

// A firewall/gateway/switch is a real NetBox device or VM, so its name links to the
// corresponding existing detail page (device → server detail, vm → VM detail).
const applianceDetailPath = (appliance: NetworkAppliance): string =>
  appliance.kind === 'device'
    ? `/datacenter/servers/${appliance.id}`
    : `/datacenter/virtual-machines/${appliance.id}`;

interface NetworkApplianceTabProps {
  resource: NetworkApplianceResource;
  /** Lowercase plural noun for empty-state copy, e.g. "firewalls". */
  nounPlural: string;
  /** Icon shown in the "none yet" empty state. */
  emptyIcon: React.ComponentType;
  /** Accessible name for the table, e.g. "Firewalls list". */
  tableAriaLabel: string;
}

function NetworkApplianceTab({ resource, nounPlural, emptyIcon: EmptyIcon, tableAriaLabel }: NetworkApplianceTabProps) {
  const { appliances, loading, error, refetch } = useNetworkAppliances(resource);

  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedKind, setSelectedKind] = useState<KindFilter>('all');
  const [isKindSelectOpen, setIsKindSelectOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [isStatusSelectOpen, setIsStatusSelectOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  useEffect(() => {
    setPage(1);
  }, [selectedKind, selectedStatus]);

  // Status dropdown options are the distinct statuses actually present in the data,
  // so the filter never offers a status that would match nothing.
  const statusOptions = useMemo(() => {
    const set = new Set<string>();
    appliances.forEach((a) => {
      if (a.status) {
        set.add(a.status);
      }
    });
    return Array.from(set).sort((x, y) => x.localeCompare(y));
  }, [appliances]);

  // Derived from the live `searchValue` (not the debounced copy) so the toolbar's
  // "Clear filters" affordance tracks the input immediately, without a 400ms lag.
  const filtersActive = searchValue.trim() !== '' || selectedKind !== 'all' || selectedStatus !== 'all';

  const filteredAppliances = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return appliances.filter((a) => {
      if (selectedKind !== 'all' && a.kind !== selectedKind) {
        return false;
      }
      if (selectedStatus !== 'all' && a.status !== selectedStatus) {
        return false;
      }
      if (q) {
        const haystack = [a.name, a.primary_ip4?.address ?? '', a.primary_ip6?.address ?? '', a.site?.name ?? '']
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [appliances, debouncedSearch, selectedKind, selectedStatus]);

  const pageCount = Math.max(1, Math.ceil(filteredAppliances.length / perPage));
  // Guard the slice against a stale, out-of-range `page`: a filter change or a
  // refetch can shrink the list below the current offset before the reset/clamp
  // effects commit, which would otherwise flash an empty table body for one frame.
  const effectivePage = Math.min(page, pageCount);

  const paginatedAppliances = useMemo(() => {
    const start = (effectivePage - 1) * perPage;
    return filteredAppliances.slice(start, start + perPage);
  }, [filteredAppliances, effectivePage, perPage]);

  // Persist the clamp back into state so later interactions start from a valid page
  // (covers the refetch-shrink case, which no filter-reset effect handles).
  useEffect(() => {
    if (page > pageCount) {
      setPage(pageCount);
    }
  }, [pageCount, page]);

  const onClearFilters = useCallback(() => {
    setSearchValue('');
    setDebouncedSearch('');
    setSelectedKind('all');
    setSelectedStatus('all');
    setPage(1);
  }, []);

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, pageNumber: number) => {
    setPage(pageNumber);
  };

  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const onKindSelect = (
    _event: React.MouseEvent<Element, MouseEvent> | undefined,
    value: string | number | undefined,
  ) => {
    if (value === 'all' || value === 'device' || value === 'vm') {
      setSelectedKind(value);
    }
    setIsKindSelectOpen(false);
  };

  const onStatusSelect = (
    _event: React.MouseEvent<Element, MouseEvent> | undefined,
    value: string | number | undefined,
  ) => {
    setSelectedStatus(value === undefined ? 'all' : String(value));
    setIsStatusSelectOpen(false);
  };

  // Only the very first load (no data yet) takes over the whole panel. A manual
  // Refresh re-runs the fetch while the toolbar + table stay mounted (the Refresh
  // button shows its own spinner), since `mountOnEnter` keeps this panel alive.
  if (loading && appliances.length === 0) {
    return <CenteredSpinner />;
  }

  if (error) {
    return (
      <EmptyState
        titleText={`Unable to load ${nounPlural}`}
        icon={ExclamationCircleIcon}
        variant="lg"
        headingLevel="h2"
        status="danger"
      >
        <EmptyStateBody>{error}</EmptyStateBody>
        <EmptyStateFooter>
          <EmptyStateActions>
            <Button variant="primary" onClick={() => refetch()}>
              Retry
            </Button>
          </EmptyStateActions>
        </EmptyStateFooter>
      </EmptyState>
    );
  }

  if (appliances.length === 0) {
    return (
      <EmptyState titleText={`No ${nounPlural} found`} icon={EmptyIcon} variant="lg" headingLevel="h2">
        <EmptyStateBody>No {nounPlural} are currently assigned to your organization.</EmptyStateBody>
      </EmptyState>
    );
  }

  const itemCount = filteredAppliances.length;

  return (
    <>
      <Toolbar>
        <ToolbarContent>
          <ToolbarItem>
            <SearchInput
              placeholder="Search by name, IP, or site..."
              value={searchValue}
              onChange={(_event, value) => setSearchValue(value)}
              onClear={() => setSearchValue('')}
            />
          </ToolbarItem>
          <ToolbarItem>
            <Select
              isOpen={isKindSelectOpen}
              selected={selectedKind}
              onSelect={onKindSelect}
              onOpenChange={(isOpen) => setIsKindSelectOpen(isOpen)}
              toggle={(toggleRef) => (
                <MenuToggle
                  ref={toggleRef}
                  onClick={() => setIsKindSelectOpen(!isKindSelectOpen)}
                  isExpanded={isKindSelectOpen}
                  aria-label="Filter by kind"
                >
                  {kindFilterLabel[selectedKind]}
                </MenuToggle>
              )}
            >
              <SelectList>
                <SelectOption value="all">All kinds</SelectOption>
                <SelectOption value="device">Device</SelectOption>
                <SelectOption value="vm">VM</SelectOption>
              </SelectList>
            </Select>
          </ToolbarItem>
          <ToolbarItem>
            <Select
              isOpen={isStatusSelectOpen}
              selected={selectedStatus}
              onSelect={onStatusSelect}
              onOpenChange={(isOpen) => setIsStatusSelectOpen(isOpen)}
              toggle={(toggleRef) => (
                <MenuToggle
                  ref={toggleRef}
                  onClick={() => setIsStatusSelectOpen(!isStatusSelectOpen)}
                  isExpanded={isStatusSelectOpen}
                  aria-label="Filter by status"
                >
                  {selectedStatus === 'all' ? 'All statuses' : selectedStatus}
                </MenuToggle>
              )}
            >
              <SelectList>
                <SelectOption value="all">All statuses</SelectOption>
                {statusOptions.map((status) => (
                  <SelectOption key={status} value={status}>
                    {status}
                  </SelectOption>
                ))}
              </SelectList>
            </Select>
          </ToolbarItem>
          <ToolbarItem>
            <Button variant="link" isInline isDisabled={!filtersActive} onClick={onClearFilters}>
              Clear filters
            </Button>
          </ToolbarItem>
          <ToolbarItem>
            <Button
              variant="secondary"
              icon={<SyncAltIcon />}
              onClick={() => refetch()}
              isLoading={loading}
              isDisabled={loading}
            >
              Refresh
            </Button>
          </ToolbarItem>
          <ToolbarItem variant="pagination">
            <Pagination
              itemCount={itemCount}
              perPage={perPage}
              page={effectivePage}
              onSetPage={onSetPage}
              onPerPageSelect={onPerPageSelect}
            />
          </ToolbarItem>
        </ToolbarContent>
      </Toolbar>

      {itemCount === 0 ? (
        <EmptyState titleText={`No ${nounPlural} match your filters`} icon={SearchIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            Try a different search term or filter, or clear the filters to see all {nounPlural}.
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={onClearFilters}>
                Clear filters
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      ) : (
        <Table aria-label={tableAriaLabel} variant="compact">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Kind</Th>
              <Th>Status</Th>
              <Th>Site</Th>
              <Th>Primary IPv4</Th>
              <Th>Primary IPv6</Th>
            </Tr>
          </Thead>
          <Tbody>
            {paginatedAppliances.map((appliance) => (
              <Tr key={`${appliance.kind}-${appliance.id}`}>
                <Td dataLabel="Name">
                  <Link to={applianceDetailPath(appliance)}>{appliance.name}</Link>
                </Td>
                <Td dataLabel="Kind">{renderKindLabel(appliance.kind)}</Td>
                <Td dataLabel="Status">{renderStatusLabel(appliance.status)}</Td>
                <Td dataLabel="Site">{appliance.site?.name ?? '—'}</Td>
                <Td dataLabel="Primary IPv4">{appliance.primary_ip4?.address ?? '—'}</Td>
                <Td dataLabel="Primary IPv6">{appliance.primary_ip6?.address ?? '—'}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      {itemCount > 0 && (
        <Pagination
          itemCount={itemCount}
          perPage={perPage}
          page={effectivePage}
          onSetPage={onSetPage}
          onPerPageSelect={onPerPageSelect}
          variant="bottom"
        />
      )}
    </>
  );
}

export default NetworkApplianceTab;
